import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  acknowledgeRunningLate,
  getAttention,
  getDashboardWeek,
  getEvents,
  getRoster,
  markEventsRead,
  markWorkerSeen,
  setRosterStar,
} from "../api/dashboardV2Api";
import type {
  AttentionItem,
  DashboardWeek,
  EventFilter,
  EventsResponse,
  RosterFilter,
  RosterQuery,
  RosterResponse,
} from "../api/dashboardV2Api";
import { QueryKeys } from "../queryKeys";

export function useDashboardWeekQuery(start?: string) {
  return useQuery<DashboardWeek>({
    queryKey: [QueryKeys.DASHBOARD_WEEK, start ?? "current"],
    queryFn: () => getDashboardWeek(start),
    staleTime: 30_000,
  });
}

export function useRosterQuery(filter: RosterFilter) {
  return useQuery<RosterResponse>({
    queryKey: [QueryKeys.ROSTER, filter],
    queryFn: () => getRoster(filter),
    staleTime: 30_000,
  });
}

/**
 * Optimistic star toggle for a roster row (SPEC §3.4). Flips the row's `starred`
 * in the cached roster page for the active filter, rolls back on error, and
 * invalidates roster + starred-worker queries on settle.
 */
export function useToggleRosterStarMutation(filter: RosterFilter) {
  const queryClient = useQueryClient();
  const key = [QueryKeys.ROSTER, filter];
  return useMutation<
    boolean,
    Error,
    { workerId: string; nextStarred: boolean },
    { previous?: RosterResponse }
  >({
    mutationFn: ({ workerId, nextStarred }) => setRosterStar(workerId, nextStarred),
    onMutate: async ({ workerId, nextStarred }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RosterResponse>(key);
      if (previous) {
        queryClient.setQueryData<RosterResponse>(key, {
          ...previous,
          items: previous.items.map((it) =>
            it.worker_id === workerId ? { ...it, starred: nextStarred } : it,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ROSTER] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.STARRED_WORKERS] });
    },
  });
}

export function useAttentionQuery(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery<Array<AttentionItem>>({
    queryKey: [QueryKeys.ATTENTION],
    queryFn: getAttention,
    staleTime: 30_000,
    ...options,
  });
}

/** Records that the employer viewed a worker's profile, dismissing that worker's
 *  new_worker_signup attention item. Refreshes the attention list on success. */
export function useMarkWorkerSeenMutation() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (workerId: string) => markWorkerSeen(workerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ATTENTION] });
    },
  });
}

/** Best-effort acknowledge (dismiss) of a "running_late" attention item, keyed by
 *  assignment id. Refreshes the attention list on settle so the item drops. */
export function useAcknowledgeRunningLateMutation() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string | number>({
    mutationFn: (assignmentId: string | number) =>
      acknowledgeRunningLate(assignmentId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ATTENTION] });
    },
  });
}

export function useEventsQuery(filter: EventFilter) {
  return useQuery<EventsResponse>({
    queryKey: [QueryKeys.EVENTS, filter],
    queryFn: () => getEvents(filter),
    staleTime: 30_000,
  });
}

/**
 * Marks the Activity feed read (server-side last_seen). Fire once when the
 * dashboard is viewed; on success, zero the unread pill in cache without a refetch.
 */
export function useMarkEventsReadMutation() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: markEventsRead,
    onSuccess: () => {
      // Zero the unread pill on the dashboard section queries only — not the
      // infinite "See all activity" query, whose data shape is InfiniteData.
      queryClient.setQueriesData<EventsResponse>(
        {
          predicate: (q) =>
            q.queryKey[0] === QueryKeys.EVENTS && q.queryKey[1] !== "infinite",
        },
        (prev) => (prev ? { ...prev, unread_count: 0 } : prev),
      );
    },
  });
}

const FULL_PAGE = 20;

/** Cursor-paginated roster for the full "See all workers" page. */
export function useInfiniteRosterQuery(filter: RosterFilter, query: RosterQuery = {}) {
  return useInfiniteQuery({
    queryKey: [QueryKeys.ROSTER, "infinite", filter, query.sort ?? null, query.dir ?? null, query.q ?? null],
    queryFn: ({ pageParam }) => getRoster(filter, FULL_PAGE, pageParam, query),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

/** Cursor-paginated activity for the full "See all activity" page. */
export function useInfiniteEventsQuery(filter: EventFilter) {
  return useInfiniteQuery({
    queryKey: [QueryKeys.EVENTS, "infinite", filter],
    queryFn: ({ pageParam }) => getEvents(filter, FULL_PAGE, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}
